<template>
  <section v-if="rows.length > 0 && store.activeRaidId !== null" class="mt-1 min-w-0 border-t border-border pt-1" :aria-label="messages.selector.title">
    <header class="flex items-center justify-between gap-2 px-2 py-1">
      <span class="min-w-0">
        <h3 class="text-xs font-semibold uppercase tracking-wide text-text-muted">{{ messages.selector.title }}</h3>
        <span class="block text-2xs text-text-muted">{{ messages.selector.hint }}</span>
      </span>
      <span class="shrink-0 text-2xs font-mono text-text-muted">{{ rows.length }}</span>
    </header>
    <div class="flex flex-col gap-1" role="group">
      <button
        v-for="row in rows"
        :key="row.key"
        type="button"
        data-build-action="select-preset"
        :data-active-build="isActive(row.preset) ? 'true' : undefined"
        :disabled="!props.canApply || applyingKey !== null"
        :class="[
          'w-full rounded-sm border p-2 text-left transition-[border-color,background-color,opacity] duration-120 ease-standard focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-60',
          isActive(row.preset) ? 'border-accent bg-accent/10' : 'border-transparent hover:border-border hover:bg-bg-surface',
        ]"
        :aria-label="messages.selector.apply(row.preset.name)"
        @click="onApply(row)"
      >
        <span class="mb-1.5 flex items-center justify-between gap-2">
          <span class="min-w-0 truncate font-mono text-xs font-semibold text-text-primary">{{ row.preset.name }}</span>
          <span class="shrink-0 rounded-sm border border-border bg-bg-surface px-1 py-0.5 text-2xs uppercase tracking-wide text-text-muted">{{ sourceLabel(row.source) }}</span>
          <span v-if="isActive(row.preset)" class="shrink-0 text-2xs font-semibold uppercase tracking-wide text-accent">✓ {{ messages.selector.active }}</span>
        </span>
        <BuildPresetPreview :preset="row.preset" show-weapon-names />
      </button>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { BuildPresetPreview } from '../build-presets'
import { buildPresetMatchesRaid } from '../../lib/buildPresets'
import { buildStageSelectionsForPlayer } from '../../lib/raidStageBuilds'
import { useGvgPlannerStore } from '../../stores/gvgPlannerStore'
import { BuildPresetSource } from '../../types'
import type { BuildPreset, BuildPresetSource as PresetSource, Player } from '../../types'
import { useBuildPresetMessages } from '../build-presets/locales'

export interface PlayerBuildPresetRowsProps {
  readonly player: Player
  readonly canApply: boolean
}

interface PresetRow {
  readonly key: string
  readonly preset: BuildPreset
  readonly source: PresetSource
}

const props = defineProps<PlayerBuildPresetRowsProps>()
const emit = defineEmits<{
  (e: 'used'): void
}>()
const store = useGvgPlannerStore()
const messages = useBuildPresetMessages()
const applyingKey = ref<string | null>(null)

const personalPresets = computed<ReadonlyArray<BuildPreset>>(() =>
  store.playerBuildPresetOwners.find((owner) =>
    owner.discordUserId === props.player.discordUserId)?.presets ?? [],
)
const rows = computed<ReadonlyArray<PresetRow>>(() => [
  ...personalPresets.value.map((preset) => ({
    key: `${BuildPresetSource.Personal}-${preset.id}`,
    preset,
    source: BuildPresetSource.Personal,
  })),
  ...store.guildBuildPresets.map((preset) => ({
    key: `${BuildPresetSource.Guild}-${preset.id}`,
    preset,
    source: BuildPresetSource.Guild,
  })),
])
const stageSelections = computed(() => buildStageSelectionsForPlayer(
  store.raidStages,
  store.raidStageDiffs,
  props.player.id,
  props.player.mainWeapons,
))

function sourceLabel(source: PresetSource): string {
  return source === BuildPresetSource.Personal
    ? messages.value.selector.personal
    : messages.value.selector.guild
}

function isActive(preset: BuildPreset): boolean {
  return buildPresetMatchesRaid(preset, store.raidStages, stageSelections.value)
}

async function onApply(row: PresetRow): Promise<void> {
  if (!props.canApply || applyingKey.value !== null || store.activeRaidId === null) return
  applyingKey.value = row.key
  const succeeded = await store.applyBuildPreset(props.player.id, row.preset.id, row.source)
  applyingKey.value = null
  if (succeeded) emit('used')
}
</script>
